import { useEffect, useRef } from 'react';
import classnames from 'classnames';

import { AvatarUnits } from 'chaika-frontend-types/frontend-cms';

import style from '../Avatar.module.scss';
import { translate } from '@chaika/i18n/helper';

// ширина контейнера аватара — 320px, поэтому 1% сдвига = 3.2px
const PX_PER_PERCENT = 3.2;

interface AvatarEditorProps {
    url: string;
    avatarUnits: AvatarUnits;
    onChangeAvatarUnits(avatarUnits: Partial<AvatarUnits>): void;
}

export function AvatarEditor({
    url,
    avatarUnits: { offsetX, offsetY, scale },
    onChangeAvatarUnits,
}: AvatarEditorProps) {
    const isChangingCoords = useRef(false);

    useEffect(() => {
        const stopCoordsChanging = () => {
            isChangingCoords.current = false;
        };

        window.addEventListener('pointerup', stopCoordsChanging);
        return () => window.removeEventListener('pointerup', stopCoordsChanging);
    }, []);

    const startCoordsChanging = (e: React.PointerEvent) => {
        if (e.button !== 0) {
            return;
        }

        isChangingCoords.current = true;
    };

    const changeAvatarUnits = (avatarUnits: Partial<AvatarUnits>) => {
        const isScaling = avatarUnits.scale !== undefined;

        if (isChangingCoords.current || isScaling) {
            onChangeAvatarUnits(avatarUnits);
        }
    };

    const moveAvatar = ({ movementX, movementY }: React.PointerEvent) => {
        changeAvatarUnits({
            offsetX: offsetX + movementX / PX_PER_PERCENT,
            offsetY: offsetY + movementY / PX_PER_PERCENT,
        });
    };

    return (
        <>
            <div className={classnames(style.avatar, style.window)}>
                <img
                    src={url}
                    alt=""
                    draggable={false}
                    className={style.image}
                    style={{
                        top: `${offsetY}%`,
                        left: `${offsetX}%`,
                        transform: `scale(${scale})`,
                        cursor: isChangingCoords.current ? 'grabbing' : 'grab',
                    }}
                    onPointerDown={startCoordsChanging}
                    onPointerMove={moveAvatar}
                />
            </div>
            <label className={style.scale}>
                <p>{translate('scale')}</p>
                <input
                    type="range"
                    min={0.1}
                    max={2}
                    step={0.01}
                    value={scale}
                    onChange={e => {
                        const { value } = e.target;
                        changeAvatarUnits({ scale: parseFloat(value) });
                    }}
                />
            </label>
        </>
    );
}
